import { useState } from 'react'
import { Button, IconButton, Typography, Grid, Box, Stack, FormHelperText, TextField, InputAdornment } from '@mui/material'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import { visuallyHidden } from '@mui/utils'
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom'
import { updatePassword } from "../../../api/login";
import { useIsMobile } from '@/hooks/Responsive'
import { useLang } from '@/i18n/useLang'
import { buildLangHref } from '@data/nav'

export default function ResetPassword() {
  const { isMobile } = useIsMobile()
  const lang = useLang() 
  const location = useLocation();
  const navigate = useNavigate();
  const { accountId, accountIdSeq } = location.state || {};

  const [form, setForm] = useState({
    temporaryPassword: '',
    newPassword: '',
    newPasswordConfirm: '',
  })
  const handleChange = (e) => {
    const { name, value } = e.target
    setForm((p) => ({ ...p, [name]: value }))
  }

  const [temporaryPassword, setTemporaryPassword] = useState(false)
  const [newPassword, setNewPassword] = useState(false)
  const [newPasswordConfirm, setNewPasswordConfirm] = useState(false)
  const [errors, setErrors] = useState({ temporaryPassword: '', newPassword: '', newPasswordConfirm: '' })

  const handleClickShowTemporaryPassword = () => setTemporaryPassword((prev) => !prev)
  const handleClickShowNewPassword = () => setNewPassword((prev) => !prev)
  const handleClickShowNewPasswordConfirm = () => setNewPasswordConfirm((prev) => !prev)

  const validate = () => {
    let tempErrors = { temporaryPassword: '', newPassword: '', newPasswordConfirm: '' }
    const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[!@#$%^&+=])[A-Za-z\d!@#$%^&+=]{10,}$/

    if (!form.temporaryPassword) {
      tempErrors.temporaryPassword = '임시비밀번호를 입력해 주세요.'
    }

    if (!form.newPassword) {
      tempErrors.newPassword = '새 비밀번호를 입력해 주세요.'
    }
    else if (!passwordRegex.test(form.newPassword)) {
      tempErrors.newPassword = '비밀번호 규정을 확인해 주세요.'
    }

    if (!form.newPasswordConfirm) {
      tempErrors.newPasswordConfirm = '새 비밀번호를 입력해 주세요.'
    }
    else if (form.newPassword !== form.newPasswordConfirm) {
      tempErrors.newPasswordConfirm = '비밀번호가 일치하지 않습니다.'
    }

    setErrors(tempErrors)
    return Object.values(tempErrors).every(x => x === '')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (validate()) {
      try {
        const params = {
          accountId : accountId,
          accountIdSeq : accountIdSeq,
          tempPassword : form.temporaryPassword,
          newPassword : form.newPassword,
        };
        const response = await updatePassword(params);

        if (response?.success) {
          navigate(buildLangHref('resetpasswordlater', lang))
        }
        else {
          alert(response?.message || "오류가 발생했습니다.");
        }
      } catch (error) {
        console.error(error);
      }
    }
  }

  return (
    <Box>
      <Typography component="h3" variant={isMobile ? 'h6' : 'h5'} sx={{ mb: 7 }}>
        비밀번호 재설정
      </Typography>

      <Stack>
        {/* 임시비밀번호 */}
        <Box sx={{ mb: 4 }}>
          <Typography fontWeight={500} sx={{ mb: 0.5 }}>
            임시비밀번호 <Typography component="span" color="error">*</Typography>
            <Box component="span" sx={visuallyHidden}>필수 입력</Box>
          </Typography>
          <TextField
            fullWidth
            id="temporaryPassword"
            name="temporaryPassword"
            type={temporaryPassword ? 'text' : 'password'}
            value={form.temporaryPassword}
            placeholder="이메일 계정으로 받은 임시비밀번호를 입력해 주세요"
            onChange={handleChange}
            error={!!errors.temporaryPassword}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ pr: 3 }}>
                  <IconButton
                    edge="end"
                    onClick={handleClickShowTemporaryPassword}
                  >
                    {temporaryPassword ? <Visibility sx={{ color: 'grey.dark' }} /> : <VisibilityOff sx={{ color: 'grey.dark' }} />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            inputProps={{ maxLength: 20 }}
          />
          {errors.temporaryPassword && (<FormHelperText error sx={{ mt: 0.5 }}>{errors.temporaryPassword}</FormHelperText>)}
        </Box>
        
        {/* 새 비밀번호 */}
        <Box sx={{ mb: 4 }}>
          <Typography fontWeight={500} sx={{ mb: 0.5 }}>
            새 비밀번호 <Typography component="span" color="error">*</Typography>
            <Box component="span" sx={visuallyHidden}>필수 입력</Box>
          </Typography>
          <TextField
            fullWidth
            id="newPassword"
            name="newPassword"
            type={newPassword ? 'text' : 'password'}
            value={form.newPassword}
            placeholder="새로 사용하실 비밀번호를 입력해 주세요"
            onChange={handleChange}
            error={!!errors.newPassword}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ pr: 3 }}>
                  <IconButton
                    onClick={handleClickShowNewPassword}
                    edge="end"
                    aria-label="toggle password visibility"
                  >
                    {newPassword ? <Visibility sx={{ color: 'grey.dark' }} /> : <VisibilityOff sx={{ color: 'grey.dark' }} />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            inputProps={{ maxLength: 20 }}
          />
          {errors.newPassword && (<FormHelperText error sx={{ mt: 0.5 }}>{errors.newPassword}</FormHelperText>)}
          <FormHelperText sx={{ mt: 0.5 }}>
            대소문자구분 영문, 숫자, 특수문자를 조합하여 10자 이상 입력. (! @ # $ % ^ & + = 사용가능)
          </FormHelperText>
        </Box>

        {/* 새 비밀번호 확인 */}
        <Box>
          <Typography fontWeight={500} sx={{ mb: 0.5 }}>
            새 비밀번호 확인 <Typography component="span" color="error">*</Typography>
            <Box component="span" sx={visuallyHidden}>필수 입력</Box>
          </Typography>
          <TextField
            fullWidth
            id="newPasswordConfirm"
            name="newPasswordConfirm"
            type={newPasswordConfirm ? 'text' : 'password'}
            value={form.newPasswordConfirm}
            placeholder="새로 사용하실 비밀번호를 다시 입력해 주세요"
            onChange={handleChange}
            error={!!errors.newPasswordConfirm}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ pr: 3 }}>
                  <IconButton
                    onClick={handleClickShowNewPasswordConfirm}
                    edge="end"
                    aria-label="toggle password visibility"
                  >
                    {newPasswordConfirm ? <Visibility sx={{ color: 'grey.dark' }} /> : <VisibilityOff sx={{ color: 'grey.dark' }} />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            inputProps={{ maxLength: 20 }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSubmit(e)
              }
            }}
          />
          {errors.newPasswordConfirm && (<FormHelperText error sx={{ mt: 0.5 }}>{errors.newPasswordConfirm}</FormHelperText>)}
        </Box>
      </Stack>

      <Button fullWidth color="primary" size="large" variant="contained" onClick={handleSubmit} sx={{ mt: 5 }}>
        확인
      </Button>
    </Box>
  )
}